/**
 * A qué comercios entra una persona de un cliente: los que ya existen (de GET /accesos/marcas) para
 * tildar, y un campo para sumar uno por su identificador (uno que existe y no está en la lista). Entre 1
 * y 20, sin repetir: lo vuelve a validar el backend, que además rechaza un comercio que no existe
 * (COMERCIO_INEXISTENTE: antes se aceptaba cualquier slug, y un error de tipeo quedaba como comercio).
 *
 * Una persona de cliente ve SÓLO estos comercios: elegir de más es mostrarle datos de otro cliente.
 * Por eso no hay «todos» ni nada tildado de entrada.
 */

import { useId, useState } from "react";
import { Plus, X } from "lucide-react";
import type { ListaMarcas } from "../campana-nueva/api";

/** El mismo identificador que valida el backend (SLUG_COMERCIO_RE). */
export const SLUG_COMERCIO_RE = /^[a-z0-9][a-z0-9-]{1,38}[a-z0-9]$/;
export const MAX_COMERCIOS = 20;

export type OpcionComercio = { brandId: string; etiqueta: string };

/** Los comercios del selector de Activa, aplanados: marca de la plataforma, «marca · comercio» o el identificador solo. */
export function opcionesDeComercios(l: ListaMarcas | null): OpcionComercio[] {
  if (!l) return [];
  const vistas = new Map<string, OpcionComercio>();
  const sumar = (brandId: string, etiqueta: string) => {
    if (brandId && !vistas.has(brandId)) vistas.set(brandId, { brandId, etiqueta });
  };
  for (const m of l.catalogo) sumar(m.brandId, m.marca ? `${m.marca} (${m.brandId})` : m.brandId);
  for (const m of l.marcas) for (const c of m.comercios) sumar(c.brandId, `${m.marca} · ${c.brandId}`);
  for (const c of l.sueltos) sumar(c.brandId, c.brandId);
  return [...vistas.values()];
}

/**
 * El nombre de cada comercio para MOSTRAR (la lista de Accesos decía el identificador: un slug, o el id de la
 * plataforma, «66a0…»): la marca de la plataforma; la marca de Activa, con el identificador sólo si esa marca tiene
 * más de un comercio («HATSU · hatsu-palermo»). Un comercio sin marca (o que no está en la lista) no va: se muestra
 * su identificador.
 */
export function nombresDeComercios(l: ListaMarcas | null): Map<string, string> {
  const out = new Map<string, string>();
  if (!l) return out;
  for (const m of l.catalogo) if (m.brandId && m.marca) out.set(m.brandId, m.marca);
  for (const m of l.marcas) {
    for (const c of m.comercios) {
      if (!c.brandId || !m.marca || out.has(c.brandId)) continue;
      out.set(c.brandId, m.comercios.length > 1 ? `${m.marca} · ${c.brandId}` : m.marca);
    }
  }
  return out;
}

export default function SelectorComercios({
  opciones,
  elegidos,
  onCambio,
  error,
}: {
  opciones: OpcionComercio[];
  elegidos: string[];
  onCambio: (comercios: string[]) => void;
  error?: string;
}) {
  const id = useId();
  const [otro, setOtro] = useState("");
  const [errorOtro, setErrorOtro] = useState("");
  // Los elegidos que no están en la lista (escritos a mano, o de una persona que ya los tenía) también se ven.
  const todas = [...opciones, ...elegidos.filter((b) => !opciones.some((o) => o.brandId === b)).map((b) => ({ brandId: b, etiqueta: b }))];

  const alternar = (brandId: string) =>
    onCambio(elegidos.includes(brandId) ? elegidos.filter((b) => b !== brandId) : [...elegidos, brandId]);

  const sumarOtro = () => {
    const slug = otro.trim().toLowerCase();
    if (!SLUG_COMERCIO_RE.test(slug)) {
      setErrorOtro("El identificador va en minúsculas, con números y guiones (3 a 40).");
      return;
    }
    if (elegidos.length >= MAX_COMERCIOS && !elegidos.includes(slug)) {
      setErrorOtro(`Hasta ${MAX_COMERCIOS} comercios.`);
      return;
    }
    setErrorOtro("");
    setOtro("");
    if (!elegidos.includes(slug)) onCambio([...elegidos, slug]);
  };

  return (
    <fieldset className="space-y-2" aria-describedby={error ? `${id}-error` : undefined}>
      <legend className="text-xs font-semibold text-slate-600">Comercios a los que entra</legend>
      {todas.length === 0 ? (
        <p className="text-[11px] text-slate-500">Todavía no hay comercios cargados: sumá el identificador abajo.</p>
      ) : (
        <div className="max-h-48 overflow-y-auto rounded-xl border border-slate-200 divide-y divide-slate-50">
          {todas.map((o) => (
            <label key={o.brandId} className="flex items-center gap-2 px-3 py-1.5 text-sm text-slate-700 cursor-pointer hover:bg-slate-50">
              <input
                type="checkbox"
                checked={elegidos.includes(o.brandId)}
                onChange={() => alternar(o.brandId)}
                className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
              />
              <span className="truncate">{o.etiqueta}</span>
            </label>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          value={otro}
          onChange={(e) => { setOtro(e.target.value); setErrorOtro(""); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); sumarOtro(); } }}
          aria-label="Identificador de otro comercio"
          placeholder="hatsu-palermo"
          className="flex-1 min-w-0 rounded-lg border border-slate-200 px-2.5 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-violet-200"
        />
        <button type="button" onClick={sumarOtro} disabled={!otro.trim()}
          className="inline-flex items-center gap-1 rounded-lg border border-slate-200 px-2.5 py-1 text-[11px] font-semibold text-slate-600 hover:border-violet-300 hover:text-violet-600 disabled:opacity-40">
          <Plus size={12} /> Sumar
        </button>
      </div>
      {errorOtro ? <p className="text-[11px] text-red-600">{errorOtro}</p> : (
        <p className="text-[11px] text-slate-400">Tiene que existir: si es una marca nueva, entrá primero a ella desde el selector de marca («Marca nueva…»).</p>
      )}
      {elegidos.length > 0 && (
        <p className="flex flex-wrap items-center gap-1 text-[11px] text-slate-500">
          {`Elegidos (${elegidos.length}):`}
          {elegidos.map((b) => (
            <span key={b} className="inline-flex items-center gap-0.5 rounded bg-violet-50 px-1.5 py-0.5 font-mono text-violet-700">
              {b}
              <button type="button" onClick={() => alternar(b)} aria-label={`Sacar ${b}`} className="hover:text-red-600"><X size={10} /></button>
            </span>
          ))}
        </p>
      )}
      {error && <p id={`${id}-error`} className="text-[11px] text-red-600">{error}</p>}
    </fieldset>
  );
}
